import React, { useMemo } from 'react';
import CardShell from '../../components/CardShell';
import { formatNumber } from '../../utils/format';
import './OfferMonthSummary.css';

/* ── Synthèse · la carte d'atterrissage (2026-09-10, refondue le 2026-10-10) :
   le CA réalisé, le portefeuille, les nouvelles ventes, l'atterrissage, face à
   la CIBLE de la version de plan (sa Trajectoire, ou un autre plan). Sous
   l'atterrissage, le plan qu'on a choisi de COMPARER : son total, puis l'écart
   coupé entre ce qui vient du RÉALISÉ (contre ce que ce plan portait sur les
   mois clos) et ce qui vient des mois À VENIR. Aucune donnée n'est
   reconstituée ici, tout vient de la vue des prévisions. */

/* Le mois de clôture s'écrit EN TOUTES LETTRES : « réalisé à fin mar » se lit
   comme une abréviation ratée, la phrase n'est pas un en-tête de colonne. */
const MOIS_LONG = (p) => [p('janvier'), p('février'), p('mars'), p('avril'), p('mai'), p('juin'),
                          p('juillet'), p('août'), p('septembre'), p('octobre'), p('novembre'), p('décembre')];

/* Un zéro se lit « – » comme partout ailleurs. */
const formatDe = (unit) => (v) => (v == null || Math.abs(v) < (unit === 'kchf' ? 500 : 0.5)
  ? '–' : formatNumber(unit === 'kchf' ? v / 1000 : v, 0));

function useSynthese(view, year) {
  return useMemo(() => {
    if (!view) return null;
    const an = String((view.years || [year])[0]);
    const ouverts = view.open || [];
    const clos = ouverts.length < 12;
    const somme = (rows, f) => rows.reduce((a, r) => a + f(r), 0);
    /* Le CA RÉALISÉ tapé dans la Synthèse (2026-10-11), le seul réalisé des ventes. */
    const reelDe = (r) => (r.lines || []).reduce((a, l) => a + ((((l.forecast || {})[an]) || {}).ca_actual || 0), 0);
    const cibles = (view.rows || []).map((r) => (r.target ? r.target.value : null)).filter((v) => v != null);
    const partDe = (prt) => (r) => ((((r.parts || {})[prt] || {}).ca) || []).slice(0, 12).reduce((x, y) => x + (y || 0), 0);
    const avecCmp = Boolean(view.compare);
    return {
      an, ouverts, clos,
      reel: clos ? somme(view.rows, reelDe) : 0,
      cible: cibles.length ? cibles.reduce((a, b) => a + b, 0) : null,
      cibleT: view.target || null,
      carnet: somme(view.rows, partDe('bk')),
      neuf: somme(view.rows, partDe('new')),
      cmp: avecCmp ? {
        plan: view.compare,
        total: somme(view.rows, (r) => (r.compare ? r.compare.value || 0 : 0)),
        clos: somme(view.rows, (r) => (r.compare ? r.compare.value_closed || 0 : 0)),
      } : null,
    };
  }, [view, year]);
}

export function OfferYearCard({ view, year, unit, p, caReel = null }) {
  const dev = unit === 'kchf' ? 'kCHF' : 'CHF';
  const nb = formatDe(unit);
  const d = useSynthese(view, year);
  /* Le plan lu : la Trajectoire de la version ouverte, ou la Trajectoire ou
     les Prévisions d'un autre plan (2026-10-10). */
  const nomPlan = (pl) => (!pl ? '' : !pl.version_id ? p('Trajectoire')
    : `${pl.kind === 'trajectory' ? p('Trajectoire') : p('Prévisions')} · ${pl.label}`);
  if (!d) return null;
  const finClos = d.clos ? MOIS_LONG(p)[(d.ouverts.length ? d.ouverts[0] : 12) - 1] : null;
  /* LE RÉALISÉ EST CELUI DE LA COMPTABILITÉ quand Finance répond (2026-09-24) :
     les saisies par offre restent le DÉTAIL, et ce qu'elles ne couvrent pas
     encore se lit sous le total. */
  const reel = caReel != null ? caReel : d.reel;
  const total = reel + d.carnet + d.neuf;
  const aVentiler = caReel != null ? caReel - d.reel : null;
  const ecart = (v) => (
    <span className={v > 0 ? 'oms-pos' : v < 0 ? 'oms-neg' : ''}>
      {Math.abs(v) < (unit === 'kchf' ? 500 : 0.5) ? '–' : `${v > 0 ? '+' : ''}${nb(v)}`}
    </span>
  );
  const CASES = 24;
  const blocs = (v, ton) => {
    const n = total > 0 && v ? Math.max(1, Math.round((v / total) * CASES)) : 0;
    return Array.from({ length: n }, (_, k) => <i key={k} className={`oms-b oms-b--${ton}`} />);
  };

  return (
    <CardShell title={`${p('Atterrissage')} ${d.an}`}>
      <div className="oms-haut">
        <div className="oms-bloc">
          <div className="oms-row">
            <span className="oms-lbl">{finClos ? `${p('CA réalisé à fin')} ${finClos}` : p('CA réalisé')}</span>
            <span className="oms-val">{d.clos ? nb(reel) : '–'}</span>
            <span className="oms-bar">{d.clos ? blocs(reel, 'reel') : null}</span>
          </div>
          <div className="oms-row">
            <span className="oms-lbl">{p('Portefeuille')}</span>
            <span className="oms-val">{nb(d.carnet)}</span>
            <span className="oms-bar">{blocs(d.carnet, 'porte')}</span>
          </div>
          <div className="oms-row">
            <span className="oms-lbl">{p('Nouvelles ventes')}</span>
            <span className="oms-val">{nb(d.neuf)}</span>
            <span className="oms-bar">{blocs(d.neuf, 'futur')}</span>
          </div>
          <div className="oms-row oms-row--tot">
            <span className="oms-lbl">{p('Atterrissage')}</span>
            <span className="oms-val">{nb(total)}</span>
            <span className="oms-bar" />
          </div>
          {d.clos && caReel != null && (
            <p className="oms-note">{p('Réalisé lu de la comptabilité')} · {p('détail par offre')} : {nb(d.reel)} {dev}
              {Math.abs(aVentiler) >= (unit === 'kchf' ? 500 : 0.5) && <> · {p('à ventiler')} : {nb(aVentiler)} {dev}</>}</p>
          )}
          {d.cmp && (
            <div className="oms-cmp">
              <div className="oms-row">
                <span className="oms-lbl">{nomPlan(d.cmp.plan)}</span>
                <span className="oms-val">{nb(d.cmp.total)}</span>
                <span className="oms-bar" />
              </div>
              <div className="oms-row">
                <span className="oms-lbl"><b>{p('Écart')}</b></span>
                <span className="oms-val">{ecart(total - d.cmp.total)}</span>
                <span className="oms-bar" />
              </div>
              <div className="oms-row oms-row--sub">
                <span className="oms-lbl">{p('dont réalisé')}{finClos ? ` · ${p('à fin')} ${finClos}` : ''}</span>
                <span className="oms-val">{ecart(reel - d.cmp.clos)}</span>
                <span className="oms-bar oms-det">{p('réalisé')} {nb(reel)} · {p('prévu')} {nb(d.cmp.clos)}</span>
              </div>
              <div className="oms-row oms-row--sub">
                <span className="oms-lbl">{p('dont à venir')}</span>
                <span className="oms-val">{ecart(d.carnet + d.neuf - (d.cmp.total - d.cmp.clos))}</span>
                <span className="oms-bar oms-det">{p('prévu')} {nb(d.carnet + d.neuf)} · {p('contre')} {nb(d.cmp.total - d.cmp.clos)}</span>
              </div>
            </div>
          )}
        </div>
        <div className="oms-cible">
          <span className="oms-cible__lbl">{p('Cible')} · {nomPlan(d.cibleT) || p('Trajectoire')}</span>
          <span className="oms-cible__val">{d.cible == null ? p('n/a') : nb(d.cible)} <small>{dev}</small></span>
          <span className="oms-cible__ecart">{p('Écart à la cible')} : {d.cible == null ? p('n/a') : ecart(total - d.cible)}</span>
          <span className="oms-cible__bar">
            {d.clos && <i className="oms-b--reel" style={{ flex: Math.max(0, reel) }} />}
            <i className="oms-b--porte" style={{ flex: Math.max(0, d.carnet) }} />
            <i className="oms-b--futur" style={{ flex: Math.max(0, d.neuf) }} />
          </span>
        </div>
      </div>
    </CardShell>
  );
}
